import React, { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Tools = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  const tools = [
    {
      name: 'Game Key Organizer',
      description: 'Organize and manage your game keys across multiple platforms. Keep track of purchased games, bundles, and activation codes in one centralized location.',
      filename: 'GameKeyOrganizer.exe',
      icon: '🔑',
      type: 'download'
    },
    {
      name: 'Game Recommender',
      description: 'Get personalized game recommendations based on your preferences and gaming history. Discover hidden gems and popular titles tailored to your taste.',
      filename: 'GameRecommender.exe',
      icon: '🎮',
      type: 'download'
    },
    {
      name: 'Steam Library Sorter',
      description: 'Sort and categorize your Steam library with ease. Create custom categories, filter by playtime, genre, and more to organize your game collection.',
      filename: 'SteamLibrarySorter.exe',
      icon: '📚',
      type: 'download'
    }
  ];

  const handleDownload = async (filename) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/tools/download/${filename}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download tool. Please try again.');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('An error occurred while downloading. Please try again.');
    }
  };

  return (
    <div className="tools-page">
      <div className="tools-container">
        <h1 className="tools-title">Gaming Tools</h1>

        <div className="tools-warning">
          <div className="warning-icon">⚠️</div>
          <div className="warning-content">
            <h3>Important Notice</h3>
            <p>
              These tools are coded in Python and compiled with PyInstaller, and might display a false virus positive.
              Please disable your antivirus if needed. All tools are safe and open for community review.
            </p>
          </div>
        </div>

        {!isAuthenticated && (
          <div className="tools-login-notice">
            <p>Please <a href="/login">log in</a> to download these tools.</p>
          </div>
        )}

        <div className="tools-grid">
          {tools.map((tool, index) => (
            <div key={index} className="tool-card">
              <div className="tool-icon">{tool.icon}</div>
              <h3 className="tool-name">{tool.name}</h3>
              <p className="tool-description">{tool.description}</p>
              {tool.type === 'download' ? (
                <button
                  className="tool-download-btn"
                  onClick={() => handleDownload(tool.filename)}
                  disabled={!isAuthenticated}
                >
                  {isAuthenticated ? '⬇️ Download' : '🔒 Login to Download'}
                </button>
              ) : (
                <button
                  className="tool-download-btn"
                  onClick={() => navigate(tool.route)}
                >
                  🚀 Use Tool
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Twitter Tools Section */}
        <div className="twitter-tools-section" style={{
          marginTop: '4rem',
          padding: '3rem',
          background: 'linear-gradient(135deg, rgba(29, 161, 242, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
          borderRadius: '24px',
          border: '2px solid rgba(29, 161, 242, 0.2)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🐦</div>
          <h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>
            Twitter Tools
          </h2>
          <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
            Access our suite of Twitter tools including Account Verification, ID Fetcher, and Winner Picker.
            All tools are optimized for minimal API usage.
          </p>
          <button
            onClick={() => navigate('/twitter-tools')}
            style={{
              padding: '1.25rem 3rem',
              background: 'linear-gradient(135deg, #1da1f2 0%, #8b5cf6 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: '1.2rem',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 8px 20px rgba(29, 161, 242, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 12px 30px rgba(29, 161, 242, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 20px rgba(29, 161, 242, 0.3)';
            }}
          >
            🚀 View Twitter Tools
          </button>
        </div>

        {/* Creator Tools Section */}
        <div className="creator-tools-section" style={{
          marginTop: '3rem',
          padding: '3rem',
          background: 'linear-gradient(135deg, rgba(255, 0, 0, 0.05) 0%, rgba(255, 193, 7, 0.05) 100%)',
          borderRadius: '24px',
          border: '2px solid rgba(255, 0, 0, 0.2)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎬</div>
          <h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: 'var(--text-primary)' }}>
            Creator Tools
          </h2>
          <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
            Powerful tools for content creators including YouTube Comment Picker for giveaways and contests.
            Fair, transparent, and optimized for creator workflows.
          </p>
          <button
            onClick={() => navigate('/creator-tools')}
            style={{
              padding: '1.25rem 3rem',
              background: 'linear-gradient(135deg, #ff0000 0%, #ffc107 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: '1.2rem',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 8px 20px rgba(255, 0, 0, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 12px 30px rgba(255, 0, 0, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 20px rgba(255, 0, 0, 0.3)';
            }}
          >
            🚀 View Creator Tools
          </button>
        </div>
      </div>
    </div>
  );
};

export default Tools;
